import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Box, LinkText, Heading, Button, Text } from '@components/common';
import { MobileContainer } from '@containers/layout';
import DefaultLoader from '@components/loading/DefaultLoader';
import DialogBox from '@components/DialogBox';
import CancelConfirm from '@containers/booking/Confirm/CancelConfirm';

import BookingActions, { BookingSelectors } from '@redux/BookingRedux';
import CleansActions, { CleansSelectors } from '@redux/CleansRedux';
import { AuthSelectors } from '@redux/AuthRedux';
import API from '@utils/api';
import { trackCleaningUpdated } from '@services/analytics';
import { getErrors } from '@utils/error';
import { checkWeekdayCoupon } from '@utils/date';

const ScrollBox = styled(Box)`
  @media screen and (min-width: 1024px) {
    height: 372px;
    overflow-y: scroll;
  }
  @media screen and (min-width: 768px) and (max-width: 1023px) {
    height: 372px;
    overflow-y: scroll;
  }
  @media screen and (max-width: 767px) {
    min-height: 285px;
    flex: 1;
  }
`;

function ServiceDate(props) {
  const {
    datesLoading,
    availableDates,
    setSelectedDate,
    selectedDate,
    onSubmit,
    isModifying,
    currentClean,
    isPMUser,
    recipient,
    updateCurrentClean,
    loadAvailableServiceDates,
    currentPropertyId
  } = props;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errMsg, setErrMsg] = useState('');

  useEffect(() => {
    if (currentPropertyId) {
      loadAvailableServiceDates();
    }
  }, [loadAvailableServiceDates, currentPropertyId]);

  const handleModifyDate = async () => {
    const {
      bookingID: bookingId,
      additionalService,
      serviceType: serviceTypeObj,
      timeframe
    } = currentClean;
    const { name: serviceType } = serviceTypeObj;

    setIsSubmitting(true);

    try {
      const { result } = await API.updateBooking(bookingId, {
        paymentMethod: 'credit',
        additionalService: (additionalService || []).map(item => item.name),
        serviceType,
        timeframe: timeframe,
        serviceDate: selectedDate.ymdString,
        isPMUser,
        recipient,
        propertyID: currentPropertyId
      });
      trackCleaningUpdated(result);
      updateCurrentClean(bookingId, result);
      setIsSubmitting(false);
      onSubmit();
    } catch (err) {
      const error = getErrors(err);
      if (error.message) {
        setErrMsg(error.message);
      } else {
        setErrMsg(Object.values(error).pop || err.message);
      }
      setIsSubmitting(false);
    }
  };

  const [show, toggleModal] = useState(false);

  const handleDissmisModal = () => {
    toggleModal(false);
  };

  return (
    <MobileContainer>
      <Box my={5}>
        <Heading p={2} mt={0}>
          What day works best for you?
        </Heading>
        {datesLoading ? (
          <Box
            flex={1}
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="Center"
          >
            <DefaultLoader size={64} />
          </Box>
        ) : (
          <ScrollBox mb={4}>
            {availableDates.map(date => {
              const isSelected = selectedDate.timestamp === date.timestamp;
              return (
                <Button
                  variant={isSelected ? 'primary' : 'secondary'}
                  width={1}
                  selected={isSelected}
                  onClick={() => {
                    setSelectedDate(date);
                    const priceChange = checkWeekdayCoupon(currentClean, date);
                    if (!isModifying || priceChange) {
                      onSubmit(priceChange);
                    }
                  }}
                  key={date.timestamp}
                  height={48}
                  fontSize={2}
                  mb={4}
                >
                  <Text textAlign="left">{date.formattedDate}</Text>
                </Button>
              );
            })}
          </ScrollBox>
        )}
        {!datesLoading && (
          <LinkText
            textAlign={['center', 'left']}
            px={2}
            mb={4}
            onClick={() => toggleModal(true)}
          >
            When can I receive service?
          </LinkText>
        )}
        {errMsg && (
          <Text pb={4} color="red" textAlign="center">
            {errMsg}
          </Text>
        )}
        {!datesLoading && isModifying && (
          <Button
            width={1}
            mb={4}
            disabled={!selectedDate || isSubmitting}
            type="button"
            onClick={handleModifyDate}
          >
            Continue
          </Button>
        )}
        <DialogBox show={show} onClickOutside={handleDissmisModal}>
          <CancelConfirm
            onCancel={handleDissmisModal}
            title="COVID-19"
            cancelLabel="Continue"
            description="Services are scheduled to resume during the month of June, and exact dates will continue to evolve as more updates are released from the CDC. Check back here for the earliest available service dates."
          />
        </DialogBox>
      </Box>
    </MobileContainer>
  );
}

ServiceDate.propTypes = {
  selectedDate: PropTypes.object,
  availableDates: PropTypes.array,
  setSelectedDate: PropTypes.func,
  onSubmit: PropTypes.func,
  datesLoading: PropTypes.bool,
  isModifying: PropTypes.bool,
  currentClean: PropTypes.object,
  isPMUser: PropTypes.bool,
  recipient: PropTypes.any,
  updateCurrentClean: PropTypes.func,
  loadAvailableServiceDates: PropTypes.func,
  currentPropertyId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
};

const mapStatesToProps = state => ({
  availableDates: BookingSelectors.selectAvailableDates(state),
  selectedDate: BookingSelectors.selectServiceDate(state),
  datesLoading: BookingSelectors.selectDatesLoading(state),
  currentClean: CleansSelectors.selectCurrentClean(state),
  isPMUser: AuthSelectors.selectIsPMUser(state),
  recipient: BookingSelectors.selectRecipientInfo(state),
  currentPropertyId: BookingSelectors.selectCurrentPropertyId(state)
});

const mapDispatchToProps = dispatch => ({
  setSelectedDate: date => dispatch(BookingActions.setBookingServiceDate(date)),
  goToStep: step => dispatch(BookingActions.setBookingStep(step)),
  updateCurrentClean: (bookingId, result) =>
    dispatch(CleansActions.setMyCleanDetailUpdateRequest(bookingId, result)),
  loadAvailableServiceDates: () => dispatch(BookingActions.fetchBookingsData())
});

export default connect(
  mapStatesToProps,
  mapDispatchToProps
)(ServiceDate);
